/* HomeSection — collapsible section wrapper for the Home page.
 * State persisted in localStorage so user choices stick across reloads.
 * Default: open. Animated chevron, smooth height transition.
 */
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

export default function HomeSection({
  id,
  title,
  eyebrow,
  badge,
  defaultOpen = true,
  accentColor = "#FF7A1A",
  children,
}) {
  const storageKey = `orio-home-${id}-open`;
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v == null ? defaultOpen : v === "1";
    } catch { return defaultOpen; }
  });

  useEffect(() => {
    try { localStorage.setItem(storageKey, open ? "1" : "0"); } catch {/* ignore */}
  }, [open, storageKey]);

  return (
    <section data-testid={`home-section-${id}`} className="mt-5">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`home-section-toggle-${id}`}
        className="w-full flex items-end justify-between px-0.5 mb-2.5 group"
      >
        <div className="text-left">
          {eyebrow && (
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: accentColor }}>
              {eyebrow}
            </div>
          )}
          <div className="font-display text-[18px] sm:text-[20px] text-[#1A1A1A] mt-0.5 flex items-center gap-2">
            {title}
            {badge != null && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold tabular-nums"
                style={{ background: `${accentColor}18`, color: accentColor }}
              >
                {badge}
              </span>
            )}
          </div>
        </div>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.25 }}
          className="h-8 w-8 rounded-full bg-[#FBF7F1] border border-[#F0EBE3] flex items-center justify-center group-hover:bg-[#F0EBE3]"
        >
          <ChevronDown className="h-4 w-4 text-[#6B6B6B]" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <div className="pt-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
